import { FirestoreTimestamp } from 'types/notifications';

/**
 * Converts a Firestore timestamp to a JavaScript Date object
 * @param timestamp Firestore timestamp object with _seconds and _nanoseconds
 * @returns JavaScript Date object
 */
export const firestoreTimestampToDate = (
  timestamp: FirestoreTimestamp,
): Date => {
  return new Date(timestamp._seconds * 1000);
};

/**
 * Formats a Firestore timestamp for relative time display (e.g., "2h ago", "Just now")
 * @param timestamp Firestore timestamp object
 * @returns Formatted relative time string
 */
export const formatRelativeTime = (timestamp: FirestoreTimestamp): string => {
  const date = firestoreTimestampToDate(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
};

/**
 * Formats a Firestore timestamp for absolute time display (e.g., "Jan 15, 2024, 2:30 PM")
 * @param timestamp Firestore timestamp object
 * @returns Formatted absolute time string
 */
export const formatAbsoluteTime = (timestamp: FirestoreTimestamp): string => {
  const date = firestoreTimestampToDate(timestamp);
  return (
    date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }) +
    ', ' +
    date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
  );
};
